/**
 * iter161 — Page Pointage Admin.
 *
 * Vue d'ensemble de TOUS les utilisateurs + édition de leur configuration
 * (timemoto_id, horaire de référence, quota congés, solde reporté).
 */
import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Clock, Settings, Users, Loader2, UserPlus, Check, X, AlertCircle, ListChecks, Plus, Edit2, Trash2, Flag, Palmtree, FileDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PointageHoraireBtn from "@/components/pointage/PointageHoraireBtn";
import { downloadAuthBlob } from "@/lib/downloadPdf";

function fmtHM(min) {
  if (min == null) return "—";
  const s = min < 0 ? "-" : "";
  const m = Math.abs(Math.round(min));
  return `${s}${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export default function PointageAdmin() {
  const [rows, setRows] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [pickedEmp, setPickedEmp] = useState({}); // { [timemoto_user_id]: user_id }
  const [justifs, setJustifs] = useState([]);
  const [period, setPeriod] = useState(monthRange());
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [pointagesUser, setPointagesUser] = useState(null);
  const [absenceUser, setAbsenceUser] = useState(null);
  const [signalements, setSignalements] = useState([]);
  const [conges, setConges] = useState([]);
  const [congesFilter, setCongesFilter] = useState("en_attente"); // iter197: en_attente | validee
  const [teamKpi, setTeamKpi] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        api.get("/pointage/admin/overview", { params: { date_from: period.from, date_to: period.to } }),
        api.get("/pointage/admin/unmapped").catch(() => ({ data: { items: [], all_employees: [] } })),
        api.get("/pointage/admin/justifications", { params: { status: "en_attente" } }).catch(() => ({ data: { items: [] } })),
        api.get("/pointage/admin/signalements", { params: { status: "en_attente" } }).catch(() => ({ data: { items: [] } })),
        api.get("/pointage/admin/conges", { params: { status: congesFilter } }).catch(() => ({ data: { items: [] } })),
        api.get("/pointage/admin/dashboard/team").catch(() => ({ data: null })),
      ]);
      setRows(r1.data.rows || []);
      setUnmapped(r2.data.items || []);
      setAllEmployees(r2.data.all_employees || []);
      setJustifs(r3.data.items || []);
      setSignalements(r4.data.items || []);
      setConges(r5.data.items || []);
      setTeamKpi(r6.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [period, congesFilter]);

  useEffect(() => { load(); }, [load]);

  const assignUnmapped = async (timemoto_user_id, user_id) => {
    if (!user_id) {
      toast.error("Sélectionnez d'abord un employé");
      return;
    }
    try {
      await api.put(`/pointage/admin/users/${user_id}/settings`, { timemoto_id: timemoto_user_id });
      toast.success("Badge rattaché — les pointages historiques sont mis à jour");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const copyTmId = async (tmId) => {
    try {
      await navigator.clipboard.writeText(tmId);
      toast.success("ID TimeMoto copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const validateJustif = async (jid) => {
    try {
      await api.post(`/pointage/admin/justifications/${jid}/validate`);
      toast.success("Justification validée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const refuseJustif = async (jid) => {
    const motif = window.prompt("Motif du refus (facultatif) :", "");
    if (motif === null) return;  // user cancelled
    try {
      await api.post(`/pointage/admin/justifications/${jid}/refuse`, { refus_motif: motif });
      toast.success("Justification refusée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const validateSignal = async (sid) => {
    try {
      await api.post(`/pointage/admin/signalements/${sid}/validate`);
      toast.success("Signalement validé — pointage corrigé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const refuseSignal = async (sid) => {
    const motif = window.prompt("Motif du refus (facultatif) :", "");
    if (motif === null) return;
    try {
      await api.post(`/pointage/admin/signalements/${sid}/refuse`, { refus_motif: motif });
      toast.success("Signalement refusé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const validateConge = async (cid) => {
    try {
      const { data } = await api.post(`/pointage/admin/conges/${cid}/validate`);
      toast.success(`Congé validé — ${data.jours_decomptes} jour(s) décompté(s) du solde`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const refuseConge = async (cid) => {
    const motif = window.prompt("Motif du refus (facultatif) :", "");
    if (motif === null) return;
    try {
      await api.post(`/pointage/admin/conges/${cid}/refuse`, { refus_motif: motif });
      toast.success("Demande refusée");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  // iter197 — Suppression définitive d'un congé (correction d'erreur de saisie admin)
  const deleteConge = async (c) => {
    const label = `${c.type} du ${c.date_from} au ${c.date_to} (${c.user_nom || c.user_login})`;
    if (!window.confirm(`Supprimer définitivement le congé :\n${label} ?\n\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/pointage/admin/conges/${c.id}`);
      toast.success("Congé supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  // iter193 — Export PDF mensuel par employé
  const exportReleveMonth = async (r) => {
    const iso = period.from || new Date().toISOString().slice(0, 10);
    const month = iso.slice(0, 7); // YYYY-MM
    try {
      await downloadAuthBlob(
        `/api/pointage/admin/users/${r.user_id}/releve/pdf?month=${month}`,
        `releve-pointage-${r.login || r.user_id}-${month}.pdf`
      );
    } catch (e) {
      toast.error(e?.message || "Erreur téléchargement PDF");
    }
  };

  const fmtH = (min) => {
    if (!min) return "0h";
    const m = Math.abs(Math.round(min));
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${h}h${String(rest).padStart(2, "0")}` : `${h}h`;
  };

  return (
    <div className="space-y-4" data-testid="pointage-admin-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-600" /> Pointage — Administration
          </h1>
          <p className="text-sm text-gray-500">
            Vue d&apos;ensemble des heures de tous les employés sur la période.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* iter339 — l'horaire du garage se règle ici, plus dans les Réglages */}
          <PointageHoraireBtn />
          <Input type="date" value={period.from} onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))} className="w-auto" data-testid="admin-period-from" />
          <span className="text-gray-400">→</span>
          <Input type="date" value={period.to} onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))} className="w-auto" data-testid="admin-period-to" />
        </div>
      </header>

      {/* iter193 — Mini-dashboard équipe (KPI mois en cours) */}
      {teamKpi && (
        <Card className="p-4 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="team-kpi-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-emerald-700" />
            <h2 className="font-semibold text-gray-900">Vue équipe — {teamKpi.month}</h2>
            <span className="text-xs text-gray-500 ml-2">({teamKpi.n_employees_actifs} employé{teamKpi.n_employees_actifs > 1 ? "s" : ""} actif{teamKpi.n_employees_actifs > 1 ? "s" : ""} au pointage)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <div className="bg-white p-3 rounded-lg border border-emerald-100" data-testid="kpi-heures-travaillees">
              <div className="text-xs text-gray-500">Heures travaillées</div>
              <div className="text-xl font-bold text-emerald-800">{fmtH(teamKpi.total_heures_travaillees_min)}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-blue-100" data-testid="kpi-heures-supp">
              <div className="text-xs text-gray-500">Heures supp.</div>
              <div className="text-xl font-bold text-blue-700">+{fmtH(teamKpi.total_heures_supp_min)}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-red-100" data-testid="kpi-retard-today">
              <div className="text-xs text-gray-500">En retard aujourd&apos;hui</div>
              <div className="text-xl font-bold text-red-700">{teamKpi.n_en_retard_aujourdhui}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-violet-100" data-testid="kpi-absences-nj">
              <div className="text-xs text-gray-500">Absences non justifiées (mois)</div>
              <div className="text-xl font-bold text-violet-700">{teamKpi.n_absences_non_justifiees_mois}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-emerald-200" data-testid="kpi-conges-en-cours">
              <div className="text-xs text-gray-500">🌴 En congé aujourd&apos;hui</div>
              <div className="text-xl font-bold text-emerald-700">{teamKpi.n_conges_en_cours}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-rose-100" data-testid="kpi-jours-maladie">
              <div className="text-xs text-gray-500">🏥 Jours maladie (mois)</div>
              <div className="text-xl font-bold text-rose-700">{teamKpi.n_jours_maladie_mois || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-orange-100" data-testid="kpi-jours-accident">
              <div className="text-xs text-gray-500">🚑 Jours accident (mois)</div>
              <div className="text-xl font-bold text-orange-700">{teamKpi.n_jours_accident_mois || 0}</div>
            </div>
          </div>
        </Card>
      )}

      {unmapped.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50" data-testid="unmapped-card">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-5 h-5 text-amber-700" />
            <h2 className="font-semibold text-amber-900">
              {unmapped.length} badge{unmapped.length > 1 ? "s" : ""} TimeMoto en attente de rattachement
            </h2>
          </div>
          <p className="text-xs text-amber-800 mb-3">
            Ces badges envoient des pointages mais ne sont pas encore liés à un employé du CRM. Choisis un employé dans la liste (ou utilise la suggestion auto si disponible).
          </p>
          <div className="space-y-2">
            {unmapped.map((u) => (
              <div key={u.timemoto_user_id} className="bg-white rounded-lg border border-amber-200 p-3" data-testid={`unmapped-${u.timemoto_user_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-sm flex-1 min-w-[260px]">
                    <div className="font-semibold text-gray-900">{u.last_full_name || "(nom inconnu)"}</div>
                    <div className="text-xs text-gray-600 font-mono flex items-center gap-2 mt-0.5">
                      <span>ID TimeMoto : {u.timemoto_user_id}</span>
                      <button
                        type="button"
                        onClick={() => copyTmId(u.timemoto_user_id)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                        data-testid={`copy-tmid-${u.timemoto_user_id}`}
                        title="Copier l'ID TimeMoto"
                      >
                        Copier
                      </button>
                      {u.last_card_number && <span>· Badge {u.last_card_number}</span>}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {u.count} pointage{u.count > 1 ? "s" : ""} reçu{u.count > 1 ? "s" : ""} · dernier le {u.last_event_at?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 min-w-[220px]">
                    {(u.suggestions || []).map((s) => (
                      <Button
                        key={s.id} size="sm" variant="outline"
                        onClick={() => assignUnmapped(u.timemoto_user_id, s.id)}
                        data-testid={`unmapped-suggest-${u.timemoto_user_id}-${s.id}`}
                        className="border-emerald-300 hover:bg-emerald-50 text-emerald-800 justify-start"
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Suggestion : {s.nom || s.login}
                      </Button>
                    ))}
                    <div className="flex items-center gap-1">
                      <select
                        className="flex-1 border rounded-md h-9 px-2 text-sm bg-white"
                        value={pickedEmp[u.timemoto_user_id] || ""}
                        onChange={(e) => setPickedEmp({ ...pickedEmp, [u.timemoto_user_id]: e.target.value })}
                        data-testid={`unmapped-select-${u.timemoto_user_id}`}
                      >
                        <option value="">— Choisir un employé —</option>
                        {allEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.nom || emp.login} {emp.role ? `(${emp.role})` : ""}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        onClick={() => assignUnmapped(u.timemoto_user_id, pickedEmp[u.timemoto_user_id])}
                        disabled={!pickedEmp[u.timemoto_user_id]}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`unmapped-assign-${u.timemoto_user_id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Lier
                      </Button>
                    </div>
                    {allEmployees.length === 0 && (
                      <span className="text-[11px] text-gray-400 italic">Tous les employés ont déjà un ID TimeMoto attribué</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {justifs.length > 0 && (
        <Card className="p-4 border-violet-300 bg-violet-50" data-testid="justifs-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-violet-700" />
            <h2 className="font-semibold text-violet-900">
              {justifs.length} justification{justifs.length > 1 ? "s" : ""} d&apos;absence en attente
            </h2>
          </div>
          <div className="space-y-2">
            {justifs.map((j) => (
              <div key={j.id} className="bg-white rounded-lg border border-violet-200 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`justif-${j.id}`}>
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">
                    {j.user_nom || j.user_login} ·{" "}
                    <span className="font-mono">{new Date(j.date).toLocaleDateString("fr-CH", { weekday: "long", day: "2-digit", month: "long" })}</span>
                  </div>
                  <div className="text-xs text-gray-700 mt-0.5">
                    Motif : <span className="font-semibold capitalize">{j.motif}</span>
                    {j.commentaire && <span className="ml-2 text-gray-500 italic">— &laquo;&nbsp;{j.commentaire}&nbsp;&raquo;</span>}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">Soumis le {j.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" onClick={() => validateJustif(j.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`justif-validate-${j.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Valider
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => refuseJustif(j.id)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`justif-refuse-${j.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {signalements.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50" data-testid="signals-card">
          <div className="flex items-center gap-2 mb-3">
            <Flag className="w-5 h-5 text-amber-700" />
            <h2 className="font-semibold text-amber-900">
              {signalements.length} signalement{signalements.length > 1 ? "s" : ""} d&apos;erreur de pointage en attente
            </h2>
          </div>
          <div className="space-y-2">
            {signalements.map((s) => (
              <div key={s.id} className="bg-white rounded-lg border border-amber-200 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`signal-${s.id}`}>
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">{s.user_nom || s.user_login}</div>
                  <div className="text-xs text-gray-700 mt-0.5 font-mono">
                    <span className="text-red-700">{s.actual?.date} {s.actual?.time} ({s.actual?.type})</span>
                    {" → "}
                    <span className="text-emerald-700">{s.expected?.date} {s.expected?.time} ({s.expected?.type})</span>
                  </div>
                  {s.commentaire && <div className="text-xs text-gray-500 italic mt-0.5">&laquo;&nbsp;{s.commentaire}&nbsp;&raquo;</div>}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" onClick={() => validateSignal(s.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`signal-validate-${s.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Valider (corrige le pointage)
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => refuseSignal(s.id)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`signal-refuse-${s.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {(conges.length > 0 || congesFilter === "validee") && (
        <Card className="p-4 border-emerald-300 bg-emerald-50" data-testid="conges-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-emerald-700" />
              <h2 className="font-semibold text-emerald-900">
                {conges.length} congé{conges.length > 1 ? "s" : ""} {congesFilter === "en_attente" ? "en attente" : "validé" + (conges.length > 1 ? "s" : "") + " (récents)"}
              </h2>
            </div>
            {/* iter197 — Toggle en_attente / validee */}
            <div className="flex items-center gap-1 text-xs">
              <Button
                size="sm"
                variant={congesFilter === "en_attente" ? "default" : "outline"}
                onClick={() => setCongesFilter("en_attente")}
                className={congesFilter === "en_attente" ? "bg-emerald-700 hover:bg-emerald-800 h-7" : "h-7"}
                data-testid="conges-filter-attente"
              >
                En attente
              </Button>
              <Button
                size="sm"
                variant={congesFilter === "validee" ? "default" : "outline"}
                onClick={() => setCongesFilter("validee")}
                className={congesFilter === "validee" ? "bg-emerald-700 hover:bg-emerald-800 h-7" : "h-7"}
                data-testid="conges-filter-validee"
              >
                Validés
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {conges.map((c) => (
              <div key={c.id} className="bg-white rounded-lg border border-emerald-200 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`conge-${c.id}`}>
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">
                    {c.user_nom || c.user_login} ·{" "}
                    <span className="font-mono text-emerald-700">{c.date_from} → {c.date_to}</span>
                    <span className="ml-2 text-gray-600">({c.jours_ouvres} j ouvré{c.jours_ouvres > 1 ? "s" : ""})</span>
                    {c.created_by_admin && <span className="ml-2 text-[10px] text-blue-600 uppercase font-bold">saisi admin</span>}
                  </div>
                  <div className="text-xs text-gray-700 mt-0.5">Type : <span className="font-semibold capitalize">{c.type}</span></div>
                  {c.commentaire && <div className="text-xs text-gray-500 italic mt-0.5">&laquo;&nbsp;{c.commentaire}&nbsp;&raquo;</div>}
                </div>
                <div className="flex items-center gap-1.5">
                  {congesFilter === "en_attente" && (
                    <>
                      <Button size="sm" onClick={() => validateConge(c.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`conge-validate-${c.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Valider
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => refuseConge(c.id)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`conge-refuse-${c.id}`}>
                        <X className="w-3.5 h-3.5 mr-1" /> Refuser
                      </Button>
                    </>
                  )}
                  {/* iter197 — Bouton supprimer (toutes vues) : correction d'erreur de saisie */}
                  <Button size="sm" variant="outline" onClick={() => deleteConge(c)} className="border-red-400 text-red-700 hover:bg-red-50" data-testid={`conge-delete-${c.id}`} title="Supprimer définitivement (erreur de saisie)">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {conges.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-4">
                Aucun congé {congesFilter === "en_attente" ? "en attente" : "validé récent"}.
              </div>
            )}
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto" data-testid="pointage-admin-table-card">
        {loading ? (
          <div className="p-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-1" /> Chargement…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-left text-[11px] uppercase tracking-wide text-gray-600">
              <tr>
                <th className="py-2 px-3">Employé</th>
                <th className="py-2 px-3">Rôle</th>
                <th className="py-2 px-3">ID TimeMoto</th>
                <th className="py-2 px-3">Pointage</th>
                <th className="py-2 px-3 text-right">Travaillé</th>
                <th className="py-2 px-3 text-right" title="Solde net = Supp − Manque. Positif = à récupérer / peut prendre en congés. Négatif = à rattraper.">Solde</th>
                <th className="py-2 px-3 text-right">Retard</th>
                <th className="py-2 px-3 text-right">À justifier</th>
                <th className="py-2 px-3 text-right">Congés</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-gray-50" data-testid={`admin-row-${r.user_id}`}>
                  <td className="py-2 px-3 font-semibold">{r.nom || r.login}</td>
                  <td className="py-2 px-3 text-xs text-gray-600">{r.role}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.timemoto_id || <span className="text-gray-400">—</span>}</td>
                  <td className="py-2 px-3">
                    {r.actif_pointage ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Actif</Badge> : <Badge variant="secondary" className="bg-gray-100 text-gray-600">Inactif</Badge>}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{fmtHM(r.minutes_worked)}</td>
                  {/* iter164.6 — Colonne unique "Solde" = Supp − Manque avec tooltip détaillé */}
                  {(() => {
                    const supp = r.heures_supp_min || 0;
                    const manque = r.heures_manquantes_min || 0;
                    const solde = supp - manque;
                    const tooltip = `Détail : +${fmtHM(supp)} supp / −${fmtHM(manque)} manque`;
                    let cell;
                    if (solde > 0) {
                      cell = <span className="text-emerald-700 font-semibold" title={`${tooltip} — À récupérer (peut prendre en congés)`}>+{fmtHM(solde)}</span>;
                    } else if (solde < 0) {
                      cell = <span className="text-red-700 font-semibold" title={`${tooltip} — À rattraper (heures dues)`}>−{fmtHM(-solde)}</span>;
                    } else {
                      cell = <span className="text-gray-400" title={tooltip}>0:00</span>;
                    }
                    return <td className="py-2 px-3 text-right font-mono cursor-help" data-testid={`admin-row-${r.user_id}-solde`}>{cell}</td>;
                  })()}
                  <td className="py-2 px-3 text-right font-mono">{r.retard_min > 0 ? <span className="text-red-700">+{fmtHM(r.retard_min)}</span> : "—"}</td>
                  <td className="py-2 px-3 text-right">{r.n_to_justify > 0 ? <Badge className="bg-violet-100 text-violet-800 border-violet-200">{r.n_to_justify}</Badge> : "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs" data-testid={`admin-row-${r.user_id}-conges-solde`}>
                    <span className={`${r.conges_solde_disponible < 0 ? "text-red-700 font-semibold" : "text-emerald-700"}`}>
                      {r.conges_solde_disponible != null ? `${r.conges_solde_disponible} j` : "—"}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => setPointagesUser(r)} data-testid={`admin-pointages-${r.user_id}`} title="Voir et éditer les pointages">
                        <ListChecks className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportReleveMonth(r)} data-testid={`admin-export-pdf-${r.user_id}`} title="Exporter le relevé PDF du mois">
                        <FileDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAbsenceUser(r)} data-testid={`admin-add-absence-${r.user_id}`} title="Ajouter une absence/congé/maladie/accident">
                        <Palmtree className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditUser(r)} data-testid={`admin-edit-${r.user_id}`}>
                        <Settings className="w-3.5 h-3.5 mr-1" /> Régler
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-gray-500">Aucun employé</td></tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      <EditSettingsDialog
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={() => { setEditUser(null); load(); }}
      />
      <PointagesDialog
        user={pointagesUser}
        onClose={() => setPointagesUser(null)}
      />
      <AddAbsenceDialog
        user={absenceUser}
        onClose={() => setAbsenceUser(null)}
        onSaved={() => { setAbsenceUser(null); load(); }}
      />
    </div>
  );
}

function EditSettingsDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setForm(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/pointage/admin/users/${user.user_id}`);
        const u = data.user || {};
        const h = data.horaire_reference || {};
        setForm({
          timemoto_id: u.timemoto_id || "",
          morning_start: h.morning_start || "08:00",
          morning_end: h.morning_end || "12:00",
          afternoon_start: h.afternoon_start || "13:30",
          afternoon_end: h.afternoon_end || "18:00",
          target_hours_day: h.target_hours_day || 8,
          quota_conges_annuel: data.conges?.quota_annuel ?? 20,
          solde_conges_reporte: data.conges?.report ?? 0,
          conges_pris_avant_demarrage: data.conges?.pris_avant_demarrage ?? 0,
          actif_pointage: !!data.actif_pointage,
          actif_pointage_depuis: data.actif_pointage_depuis || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur de chargement");
        onClose();
      }
    })();
  }, [user, onClose]);

  const save = async () => {
    if (!form) return;
    setBusy(true);
    try {
      await api.put(`/pointage/admin/users/${user.user_id}/settings`, {
        timemoto_id: form.timemoto_id || null,
        horaire_reference: {
          morning_start: form.morning_start,
          morning_end: form.morning_end,
          afternoon_start: form.afternoon_start,
          afternoon_end: form.afternoon_end,
          target_hours_day: parseFloat(form.target_hours_day) || 8,
          work_days: [1, 2, 3, 4, 5],
        },
        quota_conges_annuel: parseInt(form.quota_conges_annuel, 10) || 20,
        solde_conges_reporte: parseInt(form.solde_conges_reporte, 10) || 0,
        conges_pris_avant_demarrage: parseFloat(form.conges_pris_avant_demarrage) || 0,
        actif_pointage: !!form.actif_pointage,
        actif_pointage_depuis: form.actif_pointage_depuis || null,
      });
      toast.success("Paramètres enregistrés");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-edit-dialog">
        <DialogHeader>
          <DialogTitle>Paramètres pointage — {user?.nom || user?.login}</DialogTitle>
          <DialogDescription>ID TimeMoto, horaire de référence et quotas de congés.</DialogDescription>
        </DialogHeader>
        {form ? (
          <div className="space-y-3">
            <div>
              <Label className="text-sm">ID TimeMoto (badge employé)</Label>
              <Input
                value={form.timemoto_id}
                onChange={(e) => setForm({ ...form, timemoto_id: e.target.value })}
                placeholder="ex: 1042 ou EMP-001"
                className="font-mono mt-1"
                data-testid="admin-timemoto-id"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.actif_pointage} onCheckedChange={(v) => setForm({ ...form, actif_pointage: v })} data-testid="admin-actif-pointage" />
              <Label className="text-sm">Activer le module Pointage pour cet utilisateur</Label>
            </div>
            {form.actif_pointage && (
              <div>
                <Label className="text-sm">Suivi actif depuis le</Label>
                <Input
                  type="date"
                  value={form.actif_pointage_depuis}
                  onChange={(e) => setForm({ ...form, actif_pointage_depuis: e.target.value })}
                  className="mt-1"
                  data-testid="admin-actif-depuis"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Les jours avant cette date ne génèrent ni retard ni heures manquantes. Auto-rempli à aujourd&apos;hui si laissé vide.
                </p>
              </div>
            )}
            <div className="rounded-lg border p-3 bg-gray-50">
              <div className="text-xs font-semibold text-gray-700 mb-2">Horaire de référence (Lun–Ven)</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <TimeField label="Matin entrée" v={form.morning_start} onChange={(v) => setForm({ ...form, morning_start: v })} tid="admin-morning-start" />
                <TimeField label="Matin sortie" v={form.morning_end} onChange={(v) => setForm({ ...form, morning_end: v })} tid="admin-morning-end" />
                <TimeField label="Après-midi entrée" v={form.afternoon_start} onChange={(v) => setForm({ ...form, afternoon_start: v })} tid="admin-afternoon-start" />
                <TimeField label="Après-midi sortie" v={form.afternoon_end} onChange={(v) => setForm({ ...form, afternoon_end: v })} tid="admin-afternoon-end" />
              </div>
              <div>
                <Label className="text-xs">Heures cible / jour</Label>
                <Input type="number" step="0.25" value={form.target_hours_day} onChange={(e) => setForm({ ...form, target_hours_day: e.target.value })} className="mt-1" data-testid="admin-target-hours" />
              </div>
            </div>
            <div className="rounded-lg border p-3 bg-emerald-50/40 border-emerald-200">
              <div className="text-xs font-semibold text-emerald-900 mb-2">Congés — année en cours</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Quota annuel (jours)</Label>
                  <Input type="number" value={form.quota_conges_annuel} onChange={(e) => setForm({ ...form, quota_conges_annuel: e.target.value })} className="mt-1" data-testid="admin-quota" />
                </div>
                <div>
                  <Label className="text-xs">Report année N−1</Label>
                  <Input type="number" value={form.solde_conges_reporte} onChange={(e) => setForm({ ...form, solde_conges_reporte: e.target.value })} className="mt-1" data-testid="admin-solde-reporte" />
                </div>
                <div>
                  <Label className="text-xs">Déjà pris cette année</Label>
                  <Input type="number" step="0.5" value={form.conges_pris_avant_demarrage} onChange={(e) => setForm({ ...form, conges_pris_avant_demarrage: e.target.value })} className="mt-1" data-testid="admin-pris-avant-demarrage" placeholder="jours déjà posés" />
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-emerald-200 flex items-center justify-between">
                <span className="text-xs text-emerald-900">Solde restant à poser aujourd&apos;hui :</span>
                <span className="font-mono font-bold text-emerald-900" data-testid="admin-solde-calcule">
                  {(
                    (parseFloat(form.quota_conges_annuel) || 0)
                    + (parseFloat(form.solde_conges_reporte) || 0)
                    - (parseFloat(form.conges_pris_avant_demarrage) || 0)
                  ).toFixed(1)} j
                </span>
              </div>
              <p className="text-[10px] text-emerald-700 mt-1 leading-tight">
                Formule : <span className="font-mono">quota + report − déjà pris</span>. Phase 2 soustraira aussi les congés validés.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-edit-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy || !form} data-testid="admin-edit-save" className="bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TimeField({ label, v, onChange, tid }) {
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <Input type="time" value={v} onChange={(e) => onChange(e.target.value)} className="mt-0.5" data-testid={tid} />
    </div>
  );
}

function PointagesDialog({ user, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), time: "08:00", type: "in", commentaire: "" });
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pointage/admin/users/${user.user_id}/pointages`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) reload(); }, [user, reload]);

  const create = async () => {
    try {
      await api.post(`/pointage/admin/users/${user.user_id}/pointages`, form);
      toast.success("Pointage créé");
      setAdding(false);
      setForm({ date: new Date().toISOString().slice(0, 10), time: "08:00", type: "in", commentaire: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const createFullDay = async () => {
    if (!form.date) { toast.error("Sélectionnez d'abord une date"); return; }
    if (!window.confirm(`Créer 8 pointages (journée complète 08:00-18:00 avec pauses) pour ${user?.nom || user?.login} le ${form.date} ?`)) return;
    try {
      const { data } = await api.post(`/pointage/admin/users/${user.user_id}/pointages/journee-complete`, {
        date: form.date,
        commentaire: form.commentaire || "",
      });
      toast.success(`${data.created} pointages créés pour la journée du ${data.date}`);
      setAdding(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/pointage/admin/pointages/${editing.id}`, {
        date: editing.date, time: editing.time, type: editing.type,
        commentaire: editing._commentaire || "",
      });
      toast.success("Pointage modifié");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (pid) => {
    if (!window.confirm("Supprimer ce pointage ?")) return;
    try {
      await api.delete(`/pointage/admin/pointages/${pid}`);
      toast.success("Pointage supprimé");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="pointages-dialog">
        <DialogHeader>
          <DialogTitle>Pointages — {user?.nom || user?.login}</DialogTitle>
          <DialogDescription>Ajout manuel (oubli badge), modification ou suppression d&apos;un pointage.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end mb-2">
          <Button size="sm" onClick={() => setAdding(!adding)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pointages-add-toggle">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un pointage
          </Button>
        </div>
        {adding && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="manual-date" />
              </div>
              <div>
                <Label className="text-xs">Heure</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1" data-testid="manual-time" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <select className="mt-1 w-full border rounded-md h-9 px-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="manual-type">
                  <option value="in">Entrée</option>
                  <option value="out">Sortie</option>
                </select>
              </div>
              <div className="flex items-end gap-1">
                <Button size="sm" onClick={create} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="manual-create">Enregistrer</Button>
              </div>
            </div>
            <Input className="mt-2" placeholder="Commentaire (facultatif, ex: oubli badge)" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} data-testid="manual-commentaire" />
            {form.date && (
              <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-emerald-800">
                  <strong>Raccourci</strong> — L&apos;employé a oublié son badge toute la journée du <strong>{form.date}</strong> ?
                  <br />
                  <span className="text-[11px] text-emerald-700">
                    Crée les 8 pointages standard : 08:00→10:00 · 10:15→12:00 · 13:30→15:00 · 15:15→18:00
                  </span>
                </div>
                <Button size="sm" onClick={createFullDay} variant="outline" className="border-emerald-400 bg-white text-emerald-800 hover:bg-emerald-100" data-testid="manual-full-day">
                  <Clock className="w-3.5 h-3.5 mr-1" /> Journée complète
                </Button>
              </div>
            )}
          </div>
        )}
        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="text-center py-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">Aucun pointage sur les 30 derniers jours</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="py-2 px-2 text-left">Date</th>
                  <th className="py-2 px-2 text-left">Heure</th>
                  <th className="py-2 px-2 text-left">Type</th>
                  <th className="py-2 px-2 text-left">Source</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100" data-testid={`pointage-row-${p.id}`}>
                    {editing?.id === p.id ? (
                      <>
                        <td className="py-1 px-1"><Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="h-7 text-xs" /></td>
                        <td className="py-1 px-1"><Input type="time" value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} className="h-7 text-xs" /></td>
                        <td className="py-1 px-1">
                          <select className="border rounded h-7 text-xs px-1" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                            <option value="in">in</option>
                            <option value="out">out</option>
                          </select>
                        </td>
                        <td className="py-1 px-1 text-[10px]">{p.source || "timemoto"}</td>
                        <td className="py-1 px-1 text-right">
                          <Button size="sm" onClick={saveEdit} className="h-6 px-2 mr-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`save-${p.id}`}><Check className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-3 h-3" /></Button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 px-2 font-mono">{p.date}</td>
                        <td className="py-2 px-2 font-mono">{p.time}</td>
                        <td className="py-2 px-2">{p.type === "in" ? <span className="text-emerald-700">Entrée</span> : <span className="text-red-700">Sortie</span>}</td>
                        <td className="py-2 px-2 text-[11px] text-gray-500">
                          {p.source === "manual_admin" ? <span className="text-amber-700">Manuel admin</span> : "TimeMoto"}
                          {p.edit_log?.length > 0 && <span className="ml-1 text-orange-600" title="Modifié">●</span>}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p, _commentaire: "" })} data-testid={`edit-${p.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// iter196 — Modale de création directe d'absence (vacances/maladie/accident) par l'admin
function AddAbsenceDialog({ user, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState("vacances");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setType("vacances");
      setDateFrom(today);
      setDateTo(today);
    }
  }, [user, today]);

  if (!user) return null;

  const TYPE_LABELS = {
    vacances: "🌴 Vacances (déduit du solde annuel)",
    maladie: "🏥 Arrêt maladie",
    accident: "🚑 Accident",
  };

  const save = async () => {
    if (dateTo < dateFrom) { toast.error("La date de fin doit être postérieure à la date de début"); return; }
    setSaving(true);
    try {
      await api.post("/pointage/admin/conges/create", {
        user_id: user.user_id,
        type,
        date_from: dateFrom,
        date_to: dateTo,
      });
      toast.success(`Absence ${type} enregistrée pour ${user.nom || user.login}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-absence-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter une absence — {user.nom || user.login}</DialogTitle>
          <DialogDescription>
            Enregistrer directement des vacances, un arrêt maladie ou un accident pour cet employé. Le congé est immédiatement validé (pas de workflow d&apos;approbation).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-semibold text-gray-700">Type d&apos;absence</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="add-absence-type"
            >
              {Object.entries(TYPE_LABELS).map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Date début</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="add-absence-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Date fin</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="add-absence-to" />
            </div>
          </div>
          {type === "vacances" && (
            <p className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded">
              🌴 Ces jours seront déduits du solde annuel de vacances.
            </p>
          )}
          {(type === "maladie" || type === "accident") && (
            <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
              {type === "maladie" ? "🏥" : "🚑"} Cette absence sera comptée séparément (pas de déduction du solde vacances).
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="add-absence-save">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Enregistrement…</> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function monthRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: first.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}
